import { FilterBuilderInputMode } from "../types";

import {
  FilterValueType,
  ValueDisplayType,
  type FilterDefinition,
} from "./structured-search-schema";

import type { RequestLogStatus } from "@/types/request-status";

export type NumericInputKind = "number" | "cost" | "latency";

export enum FilterChipLayoutVariant {
  Default = "default",
  MultiValue = "multi-value",
  Provider = "provider",
  Status = "status",
}

const BETWEEN_OPERATOR = "between";

/** Trace filters store lowercase values; request logs use uppercase enum values. */
export const normalizeToRequestLogStatus = (value: string): RequestLogStatus | null => {
  const normalized = value.trim().toUpperCase();
  if (normalized === "SUCCESS" || normalized === "WARNING" || normalized === "ERROR") {
    return normalized;
  }

  return null;
};

export const resolveValuePresentation = (
  definition: FilterDefinition | undefined,
): ValueDisplayType => definition?.valueDisplay ?? ValueDisplayType.Default;

export const getNumericInputKindForType = (type: FilterValueType): NumericInputKind => {
  switch (type) {
    case FilterValueType.Cost:
      return "cost";
    case FilterValueType.Latency:
      return "latency";
    default:
      return "number";
  }
};

export const isNestedFilterType = (type: FilterValueType): boolean =>
  type === FilterValueType.Nested;

export const usesStaticEnumSuggestions = (type: FilterValueType): boolean =>
  type === FilterValueType.Enum;

export const usesStatusValuePresentation = (definition: FilterDefinition | undefined): boolean => {
  const presentation = resolveValuePresentation(definition);
  return (
    presentation === ValueDisplayType.RequestStatus || presentation === ValueDisplayType.TraceStatus
  );
};

export const resolveFilterChipLayout = (
  definition: FilterDefinition | undefined,
  isMultiValueFilter: boolean,
): FilterChipLayoutVariant => {
  const presentation = resolveValuePresentation(definition);

  switch (presentation) {
    case ValueDisplayType.Provider:
      return FilterChipLayoutVariant.Provider;
    case ValueDisplayType.RequestStatus:
    case ValueDisplayType.TraceStatus:
      return FilterChipLayoutVariant.Status;
    default:
      return isMultiValueFilter
        ? FilterChipLayoutVariant.MultiValue
        : FilterChipLayoutVariant.Default;
  }
};

export const resolveFilterBuilderInputMode = (
  type: FilterValueType,
  operator: string,
  operatorNeedsNoValue: boolean,
): FilterBuilderInputMode => {
  if (operatorNeedsNoValue) {
    return FilterBuilderInputMode.None;
  }

  const isBetween = operator === BETWEEN_OPERATOR;

  switch (type) {
    case FilterValueType.Cost:
      return isBetween ? FilterBuilderInputMode.BetweenCost : FilterBuilderInputMode.Cost;
    case FilterValueType.Latency:
      return isBetween ? FilterBuilderInputMode.BetweenLatency : FilterBuilderInputMode.Latency;
    case FilterValueType.Number:
      return isBetween ? FilterBuilderInputMode.BetweenNumber : FilterBuilderInputMode.Number;
    case FilterValueType.Date:
      return FilterBuilderInputMode.Date;
    case FilterValueType.DateRange:
      return isBetween ? FilterBuilderInputMode.DateRange : FilterBuilderInputMode.Date;
    case FilterValueType.Boolean:
      return FilterBuilderInputMode.None;
    default:
      return FilterBuilderInputMode.Text;
  }
};
